<template>
    <div class="merch-preview" :id="id" ref="preview-root">
        <div v-if="isDevMode" class="dev-control-panel">
            <h4>Dev Control Panel ({{ productType }})</h4>
            <div v-if="devConfig">
                <div v-for="(value, key) in devConfig" :key="key" class="dev-control-row">
                    <template v-if="typeof value === 'object' && value !== null">
                        <div v-for="(subValue, subKey) in value" :key="subKey" class="dev-control-subrow">
                            <label>{{ key }}.{{ subKey }}</label>
                            <input type="number" :step="getStep(key, subKey)" v-model.number="devConfig[key][subKey]" @input="applyDevConfig" />
                            <input type="range"
                                :min="getMin(key, subKey)"
                                :max="getMax(key, subKey)"
                                :step="getStep(key, subKey)"
                                v-model.number="devConfig[key][subKey]"
                                @input="applyDevConfig"
                            />
                        </div>
                    </template>
                    <template v-else>
                        <label>{{ key }}</label>
                        <input type="number" :step="getStep(key)" v-model.number="devConfig[key]" @input="applyDevConfig" />
                        <input type="range"
                            :min="getMin(key)"
                            :max="getMax(key)"
                            :step="getStep(key)"
                            v-model.number="devConfig[key]"
                            @input="applyDevConfig"
                        />
                    </template>
                </div>
                <button @click="copyDevConfig">Copy JSON</button>
            </div>
        </div>
        <div v-if="loading" class="merch-loading" ref="merch-loading">
            <div class="loading-bar-wrapper">
                <div class="loading-number-wrapper">
                    <h4 class="loading-number">{{ loadingPercentage }}%</h4>
                </div>
                <div class="loading-bar">
                    <div class="loading-bar-track">
                        <div
                            class="loading-bar-fill"
                            :style="{ width: loadingPercentage + '%' }"
                        >
                            <span class="loading-number-mobile">{{ loadingPercentage }}%</span>
                        </div>
                        <div class="loading-bar-glow" :style="{ width: loadingPercentage + '%' }"></div>
                    </div>
                </div>
                <p v-show="loadingPercentage >= 99" class="loading-stuck-text">{{ loadingStuckLabel }}</p>
            </div>
            <img
                v-if="loadingBackgroundAssetUrl !== ''"
                :src="loadingBackgroundAssetUrl"
                class="loading-merch-img"
                alt="Loading Merchandise"
            />
        </div>

        <div v-if="!loading && loadError" class="merch-error">
            <p>{{ loadError }}</p>
            <DTButton
                type="primary"
                :text="retryLabel"
                @click="retryPreview"
            />
        </div>

        <div v-if="!loading && generatedImageUrl && productType !== 'song'" :class="['merch-content', { 'is-changing': changing }]">
            <div v-if="isPlainImagePreview" :class="plainImagePreviewClasses">
                <img :src="generatedImageUrl" class="plain-preview-image" alt="Generated Preview" @error="onPreviewImageError" />
            </div>
            <div v-else class="mug-preview-wrapper">
                <img :src="productConfig.asset" class="mug-base" alt="Product Template" />
                <img v-if="curvedOverlayUrl"
                     :src="curvedOverlayUrl"
                     class="mug-overlay"
                     :style="{
                        left: productConfig.imageArea.left + '%',
                        top: productConfig.imageArea.top + '%',
                        width: productConfig.imageArea.width + '%',
                        height: productConfig.imageArea.height + '%',
                        pointerEvents: 'none',
                        zIndex: 10,
                        maxWidth: '100%',
                        maxHeight: '100%',
                     }"
                     alt="Generated Preview"
                     @error="onPreviewImageError"
                />
                <canvas ref="hiddenCanvas" style="display:none;"></canvas>
            </div>
        </div>

        <div v-if="!loading && productType === 'song' && songData" :class="['song-content', { 'is-changing': changing }]">
            <div class="song-lyrics-container">
                <div class="song-title" v-if="songData.title">{{ songData.title }}</div>
                
                <div class="song-structured">
                    <div v-for="[key, content] in songSections" :key="key" class="song-section">
                        <div class="song-section-title">{{ formatSectionTitle(key) }}</div>
                        <pre class="song-section-content">{{ content }}</pre>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="changing" class="change-indicator">
            <LoadingIndicator :size="24" />
        </div>
    </div>
</template>

<script>
import {
    ref,
    computed,
    onMounted,
    onBeforeUnmount,
    inject,
    useTemplateRef,
    watch,
    nextTick,
} from "vue";
import { useRoute } from "vue-router";
import axios from "axios";
import EventBus from "../event-bus";
import confetti from 'canvas-confetti';
import DraftService, {
    redirectToStep1,
    isDraftDataValid,
    storeDraftMeta,
    clearDraftMeta,
    getDraftIdFromUrlCached,
    getDraftPayloadFromResponse,
} from "../utils/draftService";
import LoadingIndicator from "./LoadingIndicator.vue";
import DTButton from "./DTButton.vue";
import {
    captureCustomizerException,
    captureCustomizerMessage,
    incrementCustomizerMetric,
} from "../utils/sentry";
import { createPreviewImageErrorReporter } from "../utils/previewImageReporting";
import { safeGetJson } from "../utils/safeStorage";
import {
    clearPreviewPollStart,
    runPreviewPollStartOnce,
} from "../utils/previewPollStartGuard";
import { startWhenBuilderVariantActive } from "../utils/builderVariantActivity";
import { getImageHost, getMerchProductConfig, processMerchImage } from "../utils/merchImageProcessor";
import {
    createPreviewCancellation,
    createPreviewPollStorage,
    extractPreviewPollJobId,
    extractPreviewPollResult,
    getPreviewHttpJsonConfig,
    getPreviewPollStatus,
    hasPreviewStoredValues,
    isPreviewPollCompleted,
    isPreviewPollFailed,
    isPreviewPollRunning,
    getImageUploadState,
    isMissingRequiredStep1Image,
    getStep1ImageEntryState,
    markStep1ImageRequired,
    normalizePreviewPollInterval,
    restorePreviewDraftValues,
    runPreviewRequest,
} from "../utils/previewService";
import {
    attachPollErrorDiagnostics,
    getAxiosCode,
    getAxiosHttpStatus,
    getPollElapsedMs,
    getPollStatusRequestDescriptor as createPollStatusRequestDescriptor,
    getPreviewFailureMetricAttributes,
    getPreviewFailureReason,
    getPreviewRequestDiagnostics,
    runPreviewStatusPolling,
} from "../utils/previewPollingResilience";
import { builderText, customizerText } from "../utils/customizerTexts";

export default {
    name: "MerchPreview",
    components: {
        LoadingIndicator,
        DTButton,
    },
    props: {
        id: {
            type: String,
            default: "",
        },
        requestUrl: {
            type: String,
            required: true,
        },
        requestMode: {
            type: String,
            default: "fetch",
            validator: (value) => ["fetch", "poll"].includes(value),
        },
        pollStartUrl: {
            type: String,
            default: "",
        },
        pollStatusUrl: {
            type: String,
            default: "",
        },
        pollIntervalMs: {
            type: Number,
            default: 3000,
            validator: (value) => value >= 500,
        },
        pollTimeoutMs: {
            type: Number,
            default: 360000,
            validator: (value) => value >= 0,
        },
        productType: {
            type: String,
            required: true,
        },
        minLoadingDuration: {
            type: Number,
            default: 5,
            validator: (value) => value >= 0,
        },
        loadingBackgroundAssetUrl: {
            type: String,
            default: "",
        },
        fadeInClass: {
            type: String,
            default: "",
        },
        hideAfterLoadingClass: {
            type: String,
            default: "",
        },
        loadingStuckText: {
            type: String,
            default: "",
        },
        retryButtonText: {
            type: String,
            default: "",
        },
        sectionTitleVerse1: {
            type: String,
            default: "Strophe 1",
        },
        sectionTitleVerse2: {
            type: String,
            default: "Strophe 2",
        },
        sectionTitleChorus: {
            type: String,
            default: "Refrain",
        },
        sectionTitleBridge: {
            type: String,
            default: "Bridge",
        },
    },
    setup(props) {
        const onPreviewImageError = createPreviewImageErrorReporter({
            component: "MerchPreview",
            getProductType: () => props.productType,
        });
        const pageSettings = inject("pageSettings");
        const route = useRoute();
        
        const productConfig = ref({});

        const loading = ref(true);
        const previewRoot = useTemplateRef("preview-root");
        const loadingPercentage = ref(0);
        const generatedImageUrl = ref(null);
        const merchLoading = useTemplateRef("merch-loading");
        const curvedOverlayUrl = ref(null);
        const hiddenCanvas = ref(null);
        const changing = ref(false);
        const merchData = ref(null);
        const draftData = ref(null);
        const songData = ref(null);
        const loadError = ref("");

        const isDevMode = process.env.VUE_APP_DEV_MODE === 'true';
        const devConfig = ref(null);
        const POLL_JOB_STORAGE_PREFIX = "merchPreviewPollJob";
        const POLL_CANCELLED_ERROR = "POLL_CANCELLED";
        let componentActive = true;
        let previewOutcomeReported = false;
        let previewActivated = false;
        let stopBuilderVariantActivity = null;
        
        watch(() => props.productType, (newProductType) => {
            productConfig.value = getMerchProductConfig(newProductType);
        }, { immediate: true });
        
        watch(productConfig, (val) => {
            if (isDevMode) {
                devConfig.value = JSON.parse(JSON.stringify(val));
            }
        }, { immediate: true });
        function getStep(key) {
            if (key === 'curveStrength' || key === 'curvePower') return 0.01;
            return 0.1;
        }
        function getMin(key, subKey) {
            if (key === 'left' || subKey === 'left' || key === 'top' || subKey === 'top') return 0;
            if (key === 'width' || subKey === 'width' || key === 'height' || subKey === 'height') return 1;
            if (key === 'curveStrength' || key === 'curvePower') return 0.01;
            return -100;
        }
        function getMax(key, subKey) {
            if (key === 'left' || subKey === 'left' || key === 'top' || subKey === 'top') return 100;
            if (key === 'width' || subKey === 'width' || key === 'height' || subKey === 'height') return 100;
            if (key === 'curveStrength' || key === 'curvePower') return 3;
            return 100;
        }
        function applyDevConfig() {
            if (!devConfig.value) return;
            productConfig.value = JSON.parse(JSON.stringify(devConfig.value));
            if (generatedImageUrl.value) {
                processImage(generatedImageUrl.value, (dataUrl) => {
                    curvedOverlayUrl.value = dataUrl;
                });
            }
        }
        function copyDevConfig() {
            if (!devConfig.value) return;
            navigator.clipboard.writeText(JSON.stringify(devConfig.value, null, 2));
        }

        function processImage(srcUrl, callback) {
            processMerchImage({
                srcUrl,
                canvas: hiddenCanvas.value,
                config: productConfig.value,
                // The customer still sees the untransformed image, so nothing throws;
                // report it so broken image sources show up in Sentry.
                onFallback: (reason) => {
                    captureCustomizerMessage("customizer_merch_image_processing_failed", {
                        level: "error",
                        tags: {
                            component: "MerchPreview",
                            product_type: props.productType,
                            reason,
                            image_host: getImageHost(srcUrl),
                        },
                    });
                },
            }).then(callback);
        }

        function extractPreviewImageUrl(payload) {
            if (payload?.pages) {
                const firstPageWithImage = Object.values(payload.pages).find(
                    (page) => page?.cors_img || page?.img
                );

                if (firstPageWithImage?.cors_img) {
                    return firstPageWithImage.cors_img;
                }

                if (firstPageWithImage?.img) {
                    // Backend sent no CORS-ready URL; processMerchImage has to rewrite or fall back.
                    incrementCustomizerMetric("customizer.preview_cors_img_missing", {
                        component: "MerchPreview",
                        product_type: props.productType,
                        image_host: getImageHost(firstPageWithImage.img),
                    });
                    return firstPageWithImage.img;
                }
            }

            return payload?.imageUrl || payload?.previewImageUrl || payload?.previewimageUrl || null;
        }

        const isPlainImagePreview = computed(() => productConfig.value.displayType === 'image');
        const plainImagePreviewClasses = computed(() => [
            'plain-image-preview-wrapper',
            productConfig.value.previewLayout ? `plain-image-preview-wrapper--${productConfig.value.previewLayout}` : ''
        ]);

        watch([loading, generatedImageUrl, songData], ([loadingVal, imgUrl, songDataVal]) => {
            if (!loadingVal && props.productType === 'song' && songDataVal) {
                return;
            }
            
            if (!loadingVal && imgUrl && props.productType !== 'song') {
                nextTick(() => {
                    if (isPlainImagePreview.value) {
                        return;
                    }

                    if (hiddenCanvas.value) {
                        processImage(imgUrl, (dataUrl) => {
                            curvedOverlayUrl.value = dataUrl;
                        });
                    } else {
                        if (isDevMode) console.error('[MerchPreview] Canvas nicht im DOM, verwende Original');
                        curvedOverlayUrl.value = imgUrl;
                    }
                });
            }
        });

        function changeRequested() {
            changing.value = true;
            
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }

        function changeRequestResponse(response) {
            changing.value = false;
            const payload = getDraftPayloadFromResponse(response);

            if (props.productType === 'song') {
                if (payload?.pages) {
                    songData.value = payload.pages;
                }
                
                merchData.value = payload;
                EventBus.emit("bookLoaded", payload);

                const responseDraftId = payload?.draftid || null;
                if (responseDraftId) {
                    storeDraftMeta(route.params.bookId || null, responseDraftId);
                    
                }
                return;
            }

            const imgUrl = extractPreviewImageUrl(payload);
            
            if (imgUrl) {
                generatedImageUrl.value = imgUrl;
            }
            
            merchData.value = payload;
            EventBus.emit("bookLoaded", payload);
            
            const responseDraftId = payload?.draftid || null;
            if (responseDraftId) {
                storeDraftMeta(route.params.bookId || null, responseDraftId);
                
            }
        }

        const getNormalizedRequestMode = () => props.requestMode === "poll" ? "poll" : "fetch";

        const getHttpJsonConfig = getPreviewHttpJsonConfig;

        const applyPreviewResponseData = (responseData, options = {}) => {
            const payload = getDraftPayloadFromResponse(responseData);

            if (props.productType === 'song') {
                if (payload?.pages) {
                    songData.value = payload.pages;
                }
            } else {
                const imgUrl = extractPreviewImageUrl(payload);
                generatedImageUrl.value = imgUrl;
            }

            merchData.value = payload;
            EventBus.emit("bookLoaded", payload);

            if (options.emitMerchPreviewLoaded) {
                EventBus.emit("merchPreviewLoaded", payload);
            }

            const responseDraftId = payload?.draftid || null;
            if (responseDraftId) {
                storeDraftMeta(route.params.bookId || null, responseDraftId);

            }

            return payload;
        };

        const getFailureSentryTags = (attributes, diagnostics = {}) => {
            const safeDiagnostics = getPreviewFailureMetricAttributes(diagnostics);

            return {
                ...attributes,
                ...safeDiagnostics,
                host: window.location.host,
                poll_phase: diagnostics.pollPhase || "none",
                http_status: safeDiagnostics.response_status,
                axios_code: diagnostics.axiosCode || "none",
                elapsed_ms: diagnostics.elapsedMs ?? "unknown",
                ...(diagnostics.imageUpload ? { image_upload: diagnostics.imageUpload } : {}),
                ...(diagnostics.step1ImageEntry ? { step1_image_entry: diagnostics.step1ImageEntry } : {}),
            };
        };

        const getFailureSentryContext = (diagnostics = {}) => ({
            failed_host: getPreviewFailureMetricAttributes(diagnostics).failed_host,
            finalStatusCheck: Boolean(diagnostics.finalCheck),
            variant: diagnostics.variant || route.params.bookId || null,
        });

        const reportPreviewOutcome = (outcome, failureReason = "none", diagnostics = {}) => {
            if (previewOutcomeReported) return;

            previewOutcomeReported = true;
            const attributes = {
                component: "MerchPreview",
                product_type: props.productType,
                request_mode: props.requestMode === "poll" ? "poll" : "fetch",
                outcome,
                failure_reason: failureReason,
            };

            incrementCustomizerMetric(
                "customizer.preview_load",
                outcome === "failure"
                    ? { ...attributes, ...getPreviewFailureMetricAttributes(diagnostics) }
                    : attributes
            );

            // A missing draft is counted above, not reported: it comes from bad links, not our code.
            if (outcome === "failure" && failureReason !== "draft_not_found") {
                captureCustomizerMessage("customizer_preview_load_failed", {
                    level: "error",
                    tags: getFailureSentryTags(attributes, diagnostics),
                    context: getFailureSentryContext(diagnostics),
                });
            }
        };

        const finishPreviewLoading = () => {
            if (!componentActive) return;

            reportPreviewOutcome("success");

            if (merchLoading.value) {
                merchLoading.value.style.opacity = 0;
            }

            if (props.fadeInClass !== "") {
                const contentBoxes = document.getElementsByClassName(props.fadeInClass);
                for (let i = 0; i < contentBoxes.length; i++) {
                    contentBoxes[i].style.display = "flex";
                    contentBoxes[i].style.transition = "opacity 0.3s";
                    contentBoxes[i].style.opacity = 1;
                }
            }

            if (props.hideAfterLoadingClass !== "") {
                const elementsToHide = document.getElementsByClassName(props.hideAfterLoadingClass);
                for (let i = 0; i < elementsToHide.length; i++) {
                    elementsToHide[i].style.transition = "opacity 0.3s";
                    elementsToHide[i].style.opacity = 0;
                    setTimeout(() => {
                        elementsToHide[i].style.display = "none";
                    }, 300);
                }
            }

            setTimeout(() => {
                if (!componentActive) return;
                loading.value = false;
                EventBus.emit("stepThreeShowContent");
                showConfetti();
            }, 300);
        };

        const pollCancellation = createPreviewCancellation({
            cancelCode: POLL_CANCELLED_ERROR,
            isActive: () => componentActive,
        });
        const pollStorage = createPreviewPollStorage({
            prefix: POLL_JOB_STORAGE_PREFIX,
            getBookId: () => route.params.bookId,
            getProductType: () => props.productType,
            includeProductTypeInKey: true,
            getPollStartUrl: () => props.pollStartUrl,
            getPollStatusUrl: () => props.pollStatusUrl,
            getPollTimeoutMs: () => props.pollTimeoutMs,
        });
        const getPollJobStorageKey = pollStorage.getStorageKey;
        const normalizePollIntervalMs = (value, fallback = props.pollIntervalMs) => (
            normalizePreviewPollInterval(value, fallback)
        );
        const clearPollTimeoutHandle = pollCancellation.clear;
        const isPollCancelledError = pollCancellation.isCancelled;
        const assertPollActive = pollCancellation.assertActive;
        const waitForPollInterval = pollCancellation.wait;
        const isPollJobExpired = pollStorage.isExpired;
        const getStoredPollJob = pollStorage.get;
        const persistPollJob = pollStorage.persist;
        const clearStoredPollJob = pollStorage.clear;
        const clearStoredPollJobForRequest = pollStorage.clearForRequest;

        const getPollStatusValue = getPreviewPollStatus;
        const isPollCompleted = isPreviewPollCompleted;
        const isPollFailed = isPreviewPollFailed;
        const isPollRunning = isPreviewPollRunning;
        const extractPollJobId = extractPreviewPollJobId;
        const extractPollResult = (payload) => extractPreviewPollResult(
            payload,
            (candidate) => Boolean(
                candidate.pages || candidate.imageUrl ||
                candidate.previewImageUrl || candidate.previewimageUrl
            )
        );

        const buildPollDiagnostics = ({
            error = null,
            pollJob = null,
            descriptor = null,
            response = null,
            pollPhase = "status",
            failureReason = "request_failed",
            finalCheck = false,
        } = {}) => ({
            component: "MerchPreview",
            productType: props.productType,
            requestMode: getNormalizedRequestMode(),
            jobId: pollJob?.jobId || null,
            pollPhase,
            requestUrl: descriptor?.url || (pollPhase === "start" ? props.pollStartUrl : props.pollStatusUrl),
            httpStatus: response?.status || getAxiosHttpStatus(error),
            axiosCode: getAxiosCode(error),
            elapsedMs: getPollElapsedMs(pollJob),
            host: window.location.host,
            variant: pollJob?.variantID || route.params.bookId || null,
            failureReason,
            finalCheck,
        });

        const createPollError = (message, failureReason, diagnostics = {}) => (
            attachPollErrorDiagnostics(new Error(message), {
                ...diagnostics,
                failureReason,
            })
        );

        const ensurePollConfig = () => {
            if (!props.pollStartUrl || !props.pollStatusUrl) {
                throw new Error("Polling ist nicht vollständig konfiguriert.");
            }
        };

        const getPollStatusRequestDescriptor = (jobId) => (
            createPollStatusRequestDescriptor(props.pollStatusUrl, jobId)
        );

        const getPollStatusRequest = (descriptor) => {
            if (descriptor.method === "get") {
                // sentry-ignore: status checks are retried; the final outcome is reported via reportPreviewOutcome
                return axios.get(descriptor.url, { ...getHttpJsonConfig(30000), sentryReport: false });
            }

            // sentry-ignore: status checks are retried; the final outcome is reported via reportPreviewOutcome
            return axios.post(descriptor.url, descriptor.body, { ...getHttpJsonConfig(30000), sentryReport: false });
        };

        const evaluatePollPayload = (payload, storageKey, requestBody, fallbackIntervalMs, requireKnownStatus = true, diagnostics = {}) => {
            const status = getPollStatusValue(payload);

            if (isPollCompleted(payload)) {
                const result = extractPollResult(payload);
                if (!result) {
                    throw createPollError(
                        "Polling wurde abgeschlossen, aber es fehlt result.",
                        "missing_result",
                        diagnostics
                    );
                }

                applyPreviewResponseData(result, {
                    emitMerchPreviewLoaded: true,
                    logLabel: "Poll preview response received",
                });
                clearStoredPollJob(storageKey);
                return { done: true, pollJob: null, intervalMs: fallbackIntervalMs };
            }

            if (isPollFailed(payload)) {
                clearStoredPollJob(storageKey);
                throw createPollError(
                    payload?.message || payload?.error || "Preview generation failed",
                    "backend_failed",
                    diagnostics
                );
            }

            if (requireKnownStatus && (!status || !isPollRunning(payload))) {
                throw createPollError(
                    `Unbekannter Polling-Status: ${status || "leer"}`,
                    "unknown_status",
                    diagnostics
                );
            }

            const existingPollJob = safeGetJson(storageKey, {});
            const intervalMs = normalizePollIntervalMs(payload?.pollIntervalMs, fallbackIntervalMs);
            const pollJob = persistPollJob(storageKey, requestBody, {
                jobId: extractPollJobId(payload) || existingPollJob.jobId,
                status: status || "processing",
                pollIntervalMs: intervalMs,
                expiresAt: payload?.expiresAt || existingPollJob.expiresAt || null,
            });

            return { done: false, pollJob, intervalMs };
        };

        const pollPreviewJob = async (initialPollJob, requestBody, initialIntervalMs = props.pollIntervalMs) => {
            return runPreviewStatusPolling({
                initialPollJob,
                requestBody,
                initialIntervalMs,
                pollTimeoutMs: props.pollTimeoutMs,
                waitForPollInterval,
                assertPollActive,
                isPollJobExpired,
                clearStoredPollJob,
                persistPollJob,
                normalizePollIntervalMs,
                getStatusRequestDescriptor: getPollStatusRequestDescriptor,
                requestStatus: getPollStatusRequest,
                getStatusPayload: (response) => getDraftPayloadFromResponse(response.data),
                evaluateStatusPayload: evaluatePollPayload,
                buildDiagnostics: buildPollDiagnostics,
                isCancelledError: isPollCancelledError,
            });
        };

        const startPollingPreview = async (requestBody) => {
            ensurePollConfig();
            const storageKey = getPollJobStorageKey(requestBody);
            const storedPollJob = getStoredPollJob(requestBody);

            if (storedPollJob) {
                return pollPreviewJob(storedPollJob, requestBody, storedPollJob.pollIntervalMs);
            }

            const startPollJob = {
                jobId: null,
                createdAt: Date.now(),
                variantID: requestBody.variantID,
            };
            const startDescriptor = {
                method: "post",
                url: props.pollStartUrl,
                body: null,
            };
            let startResponse;

            try {
                startResponse = await runPreviewPollStartOnce(
                    storageKey,
                    ({ idempotencyKey }) => axios.post(
                        props.pollStartUrl,
                        { ...requestBody, idempotencyKey },
                        // sentry-ignore: the start is retried on network errors; the final outcome is reported via reportPreviewOutcome
                        { ...getHttpJsonConfig(30000), sentryReport: false }
                    ),
                    { isActive: () => componentActive }
                );
            } catch (error) {
                throw attachPollErrorDiagnostics(error, buildPollDiagnostics({
                    error,
                    pollJob: startPollJob,
                    descriptor: startDescriptor,
                    pollPhase: "start",
                    failureReason: "request_failed",
                }));
            }

            const startPayload = getDraftPayloadFromResponse(startResponse.data);
            const jobId = extractPollJobId(startPayload);
            const startDiagnostics = buildPollDiagnostics({
                pollJob: {
                    ...startPollJob,
                    jobId,
                },
                descriptor: startDescriptor,
                response: startResponse,
                pollPhase: "start",
            });

            if (!jobId && isPollFailed(startPayload)) {
                throw createPollError(
                    startPayload?.message || startPayload?.error || "Preview generation failed",
                    "backend_failed",
                    startDiagnostics
                );
            }

            if (!jobId && isPollCompleted(startPayload)) {
                const result = extractPollResult(startPayload);
                if (!result) {
                    throw createPollError(
                        "Polling wurde abgeschlossen, aber es fehlt result.",
                        "missing_result",
                        startDiagnostics
                    );
                }

                applyPreviewResponseData(result, {
                    emitMerchPreviewLoaded: true,
                    logLabel: "Poll preview response received",
                });
                return;
            }

            if (!jobId) {
                throw createPollError(
                    "Polling konnte nicht gestartet werden: jobId fehlt.",
                    "missing_job_id",
                    startDiagnostics
                );
            }

            const startedPollJob = persistPollJob(storageKey, requestBody, {
                jobId,
                status: getPollStatusValue(startPayload) || "queued",
                pollIntervalMs: normalizePollIntervalMs(startPayload?.pollIntervalMs, props.pollIntervalMs),
                expiresAt: startPayload?.expiresAt || null,
                createdAt: Date.now(),
            });

            const startEvaluation = evaluatePollPayload(
                { ...(startPayload || {}), jobId },
                storageKey,
                requestBody,
                startedPollJob.pollIntervalMs,
                false,
                startDiagnostics
            );

            if (startEvaluation.done) {
                return;
            }

            return pollPreviewJob(
                { ...startEvaluation.pollJob, jobId },
                requestBody,
                startEvaluation.intervalMs
            );
        };

        const makeApiRequest = (requestBody, options = {}) => {
            const allowRedirect = options.allowRedirect !== false;
            const requestMode = getNormalizedRequestMode();
            let fetchPreviewSuccess = true;
            let previewRequestError = null;
            let previewRequestDiagnostics = {};

            if (requestMode === "poll" && options.forceNewPollJob === true) {
                clearStoredPollJobForRequest(requestBody);
                clearPreviewPollStart(getPollJobStorageKey(requestBody));
            }
            
            const previewRequestPromise = runPreviewRequest({
                requestMode,
                requestBody,
                minLoadingDuration: props.minLoadingDuration,
                setLoadingPercentage: (value) => { loadingPercentage.value = value; },
                startPolling: startPollingPreview,
                fetchPreview: (body) => axios.post(
                    props.requestUrl,
                    body,
                    // sentry-ignore: the preview outcome is reported via reportPreviewOutcome
                    { ...getHttpJsonConfig(360000), sentryReport: false }
                ),
                applyFetchResult: (response) => {
                        applyPreviewResponseData(response.data, {
                            emitMerchPreviewLoaded: true,
                            logLabel: "API Response received",
                        });
                },
            }).catch((error) => {
                fetchPreviewSuccess = false;
                previewRequestError = error;
                previewRequestDiagnostics = getPreviewRequestDiagnostics(error, {
                    requestMode,
                    requestUrl: props.requestUrl,
                });
                const safeDiagnostics = getPreviewFailureMetricAttributes(
                    previewRequestDiagnostics
                );

                if (isPollCancelledError(error)) {
                    return;
                }
                
                captureCustomizerException(error, {
                    tags: {
                        component: "MerchPreview",
                        action: "loadPreview",
                        requestMode,
                        request_mode: requestMode,
                        variant: requestBody.variantID,
                        productType: props.productType,
                        product_type: props.productType,
                        host: window.location.host,
                        job_id: safeDiagnostics.job_id,
                        response_status: safeDiagnostics.response_status,
                        failed_host: safeDiagnostics.failed_host,
                        poll_phase: previewRequestDiagnostics.pollPhase || "none",
                        http_status: safeDiagnostics.response_status,
                        axios_code: previewRequestDiagnostics.axiosCode || "none",
                        elapsed_ms: previewRequestDiagnostics.elapsedMs ?? "unknown",
                    },
                    context: {
                        failed_host: safeDiagnostics.failed_host,
                        finalStatusCheck: Boolean(previewRequestDiagnostics.finalCheck),
                    },
                });
            });

            Promise.resolve(previewRequestPromise)
                .then(() => {
                    if (!fetchPreviewSuccess) {
                        if (!isPollCancelledError(previewRequestError)) {
                            showLoadError(
                                customizerText("previewLoadError"),
                                getPreviewFailureReason(previewRequestError, "request_failed"),
                                previewRequestDiagnostics
                            );
                        }
                        return;
                    }
                    finishPreviewLoading();
                })
                .catch(error => {
                    captureCustomizerException(error, {
                        tags: { component: "MerchPreview", failure_reason: "draft_load_promise_failed" },
                    });
                    if (allowRedirect) {
                        redirectToStep1('Draft loading promise failed', pageSettings);
                    } else {
                        showLoadError(
                            customizerText("draftLoadError"),
                            "unexpected_load_failure"
                        );
                    }
                });
        };

        const saveDraftValuesToStorage = (loadedDraftData) => {
            restorePreviewDraftValues(loadedDraftData, pageSettings);
        };
        
        const hasStoredValues = hasPreviewStoredValues;
        
        const showLoadError = (message, failureReason = "unknown", diagnostics = {}) => {
            reportPreviewOutcome("failure", failureReason, diagnostics);
            loadError.value = message;
            loading.value = false;
            if (merchLoading.value) {
                merchLoading.value.style.opacity = 0;
            }
        };
        
        const handleDraftLoadFailure = (
            reason,
            draftLoadTriggered = false,
            loadedDraftData = null,
            diagnostics = {}
        ) => {
            if (draftLoadTriggered) {
                if (loadedDraftData) {
                    saveDraftValuesToStorage(loadedDraftData);
                }
                
                const hasStepData = hasStoredValues(pageSettings?.step1StorageKey);
                
                if (hasStepData) {
                    loadPreviewNormal({ allowRedirect: false });
                } else {
                    showLoadError(
                        customizerText("draftLoadError"),
                        diagnostics.httpStatus === 404 ? "draft_not_found" : "draft_load_failed",
                        diagnostics
                    );
                }
                return;
            }
            
            redirectToStep1(reason, pageSettings);
        };

        const loadPreview = () => {
            const draftId = getDraftIdFromUrlCached();
            const bookId = route.params.bookId || null;
            
            if (draftId) {
                storeDraftMeta(bookId, draftId);
                
                DraftService.loadDraftData(draftId).then(result => {
                    if (result.success && isDraftDataValid(result.data)) {
                        const loadedDraftData = result.data;
                        draftData.value = loadedDraftData;
                        saveDraftValuesToStorage(loadedDraftData);
                        
                        if (props.productType === 'song') {
                            if (loadedDraftData.pages) {
                                songData.value = loadedDraftData.pages;
                            }
                        } else {
                            const imgUrl = extractPreviewImageUrl(loadedDraftData);
                            
                            if (imgUrl) {
                                generatedImageUrl.value = imgUrl;
                            }
                        }
                        
                        merchData.value = loadedDraftData;
                        EventBus.emit("bookLoaded", loadedDraftData);
                        reportPreviewOutcome("success");
                        
                        merchLoading.value.style.opacity = 0;
                        
                        if (props.fadeInClass !== "") {
                            const contentBoxes = document.getElementsByClassName(props.fadeInClass);
                            for (let i = 0; i < contentBoxes.length; i++) {
                                contentBoxes[i].style.display = "flex";
                                contentBoxes[i].style.transition = "opacity 0.3s";
                                contentBoxes[i].style.opacity = 1;
                            }
                        }
                        
                        if (props.hideAfterLoadingClass !== "") {
                            const elementsToHide = document.getElementsByClassName(props.hideAfterLoadingClass);
                            for (let i = 0; i < elementsToHide.length; i++) {
                                elementsToHide[i].style.transition = "opacity 0.3s";
                                elementsToHide[i].style.opacity = 0;
                                setTimeout(() => {
                                    elementsToHide[i].style.display = "none";
                                }, 300);
                            }
                        }
                        
                        setTimeout(() => {
                            loading.value = false;
                            EventBus.emit("stepThreeShowContent");
                            showConfetti();
                        }, 300);
                        
                        return;
                    } else {
                        handleDraftLoadFailure(
                            'Draft loading failed or invalid',
                            true,
                            result.data,
                            result.diagnostics
                        );
                    }
                }).catch(error => {
                    captureCustomizerException(error, {
                        tags: { component: "MerchPreview", failure_reason: "draft_load_promise_failed" },
                    });
                    handleDraftLoadFailure('Draft loading promise failed', true);
                });
            } else {
                clearDraftMeta();
                loadPreviewNormal();
            }
        }

        const loadPreviewNormal = (options = {}) => {
            const allowRedirect = options.allowRedirect !== false;
            if (!pageSettings) {
                if (allowRedirect) {
                    redirectToStep1('pageSettings not injected', null);
                } else {
                    showLoadError(
                        customizerText("draftLoadError"),
                        "page_settings_missing"
                    );
                }
                return;
            }
            
            const step1Values = safeGetJson(pageSettings.step1StorageKey, {});
            const step2Values = safeGetJson(pageSettings.step2StorageKey, {});
            const variantId = pageSettings.variant || "";

            if (Object.keys(step1Values).length === 0) {
                if (allowRedirect) {
                    redirectToStep1('step1Values is empty', pageSettings);
                } else {
                    showLoadError(
                        customizerText("draftLoadError"),
                        "missing_customizer_data"
                    );
                }
                return;
            }

            if (isMissingRequiredStep1Image(props.productType, step1Values)) {
                // Without the photo the backend only skips the preview
                // (missing_required_image), so send the customer back to the upload.
                const step1ImageEntry = getStep1ImageEntryState(step1Values);
                markStep1ImageRequired(pageSettings.step1StorageKey, step1Values);
                if (allowRedirect) {
                    reportPreviewOutcome("failure", "missing_required_image", {
                        imageUpload: getImageUploadState(pageSettings.step1StorageKey),
                        step1ImageEntry,
                    });
                    redirectToStep1("missing_required_image", pageSettings);
                } else {
                    showLoadError(
                        customizerText("draftLoadError"),
                        "missing_required_image"
                    );
                }
                return;
            }

            const requestBody = {
                step1Values: step1Values,
                step2Values: step2Values,
                variantID: variantId,
                productType: props.productType,
                minLoadingDuration: props.minLoadingDuration,
                currentUrl: window.location.href
            };
            
            makeApiRequest(requestBody, {
                allowRedirect,
                forceNewPollJob: options.forceNewPollJob === true,
            });
        };

        const retryPreview = () => {
            previewOutcomeReported = false;
            loadError.value = "";
            loadingPercentage.value = 0;
            loading.value = true;

            nextTick(() => {
                if (merchLoading.value) {
                    merchLoading.value.style.opacity = 1;
                }

                loadPreviewNormal({
                    allowRedirect: false,
                    forceNewPollJob: true,
                });
            });
        };

        const showConfetti = () => {
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 },
                colors: [
                    '#7EBBCC',
                    '#F99B70',
                    '#D8ECF2',
                    '#57a5bb',
                    '#4D92A5',
                    '#FCD5C3',
                    '#D99272',
                    '#B37457',
                    '#2B2B2B'
                ]
            });
        };

        const activatePreview = () => {
            if (!componentActive || previewActivated) {
                return;
            }

            previewActivated = true;
            EventBus.on("changeRequestSubmitted", changeRequested);
            EventBus.on("changeRequestResponse", changeRequestResponse);
            loadPreview();
        };

        onMounted(() => {
            nextTick(() => {
                if (!componentActive) return;

                stopBuilderVariantActivity = startWhenBuilderVariantActive(
                    previewRoot.value,
                    activatePreview
                );
            });
        });

        onBeforeUnmount(() => {
            componentActive = false;
            stopBuilderVariantActivity?.();
            stopBuilderVariantActivity = null;
            clearPollTimeoutHandle();
            EventBus.off("changeRequestSubmitted", changeRequested);
            EventBus.off("changeRequestResponse", changeRequestResponse);
        });

        watch(merchData, (newValue) => {
            EventBus.emit("bookChanged", newValue);
        });

        const formatSectionTitle = (key) => {
            const titleMap = {
                verse1: props.sectionTitleVerse1,
                verse2: props.sectionTitleVerse2,
                chorus1: props.sectionTitleChorus,
                chorus2: props.sectionTitleChorus,
                chorus3: props.sectionTitleChorus,
                bridge: props.sectionTitleBridge
            };
            
            return titleMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
        };

        const getSectionOrder = (key) => {
            const orderMap = {
                verse1: 1,
                chorus1: 2,
                verse2: 3,
                chorus2: 4,
                bridge: 5,
                chorus3: 6
            };
            
            return orderMap[key] || 7;
        };

        const songSections = computed(() => {
            if (!songData.value) return [];
            
            return Object.entries(songData.value)
                .filter(([key]) => key !== 'title')
                .sort(([keyA], [keyB]) => getSectionOrder(keyA) - getSectionOrder(keyB));
        });

        const loadingStuckLabel = computed(() => builderText(props.loadingStuckText, "merchLoadingStuck"));
        const retryLabel = computed(() => builderText(props.retryButtonText, "retry"));

        return {
            loadingStuckLabel,
            retryLabel,
            onPreviewImageError,
            loading,
            previewRoot,
            loadingPercentage,
            merchLoading,
            generatedImageUrl,
            productConfig,
            isPlainImagePreview,
            plainImagePreviewClasses,
            curvedOverlayUrl,
            hiddenCanvas,
            isDevMode,
            devConfig,
            getStep,
            getMin,
            getMax,
            applyDevConfig,
            copyDevConfig,
            changing,
            merchData,
            draftData,
            songData,
            songSections,
            formatSectionTitle,
            loadError,
            retryPreview
        };
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.mixins.scss" as *;

.merch-preview {
    position: relative;
    display: block;
    width: 100%;

    .merch-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        row-gap: 24px;
        width: 100%;
        transition: opacity 0.3s;

        @media (max-width: $breakpoint-mobile) {
            row-gap: 16px;
        }

        .loading-bar-wrapper {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            row-gap: 12px;
            width: 100%;
            max-width: 500px;

            @media (max-width: $breakpoint-mobile) {
                order: 1;
                max-width: unset;
                row-gap: 16px;
            }

            .loading-number-wrapper {
                width: 100%;
                text-align: center;

                @media (max-width: $breakpoint-mobile) {
                    display: none;
                }

                .loading-number {
                    color: $color-new-black;
                    display: inline;
                    font-weight: 600;
                    font-size: 20px;
                }
            }

            .loading-bar {
                width: 100%;
                height: 18px;
                position: relative;

                @media (max-width: $breakpoint-mobile) {
                    height: 24px;
                }

                .loading-bar-track {
                    width: 100%;
                    height: 100%;
                    background: $color-light-gray;
                    border-radius: 18px;
                    overflow: hidden;
                    position: relative;
                    box-shadow:
                        inset 0 2px 6px rgba(0, 0, 0, 0.15),
                        0 4px 12px rgba(126, 187, 204, 0.25);

                    @media (max-width: $breakpoint-mobile) {
                        border-radius: 24px;
                    }

                    .loading-bar-fill {
                        height: 100%;
                        background: linear-gradient(
                            90deg,
                            $color-primary-dt-blue 0%,
                            rgba($color-primary-dt-blue, 0.3) 20%,
                            transparent 40%,
                            rgba($color-secondary-dt-orange, 0.3) 60%,
                            $color-secondary-dt-orange 80%,
                            rgba($color-secondary-dt-orange, 0.3) 90%,
                            transparent 100%
                        );
                        background-size: 300% 100%;
                        border-radius: 18px;
                        position: relative;
                        overflow: hidden;
                        transition: width 0.15s ease-out;
                        box-shadow: 0 4px 12px rgba(126, 187, 204, 0.5);
                        animation: gradientShift 12s ease-in-out infinite;

                        @media (max-width: $breakpoint-mobile) {
                            border-radius: 24px;
                        }

                        .loading-number-mobile {
                            display: none;
                            position: absolute;
                            right: 8px;
                            top: 50%;
                            transform: translateY(-50%);
                            font-size: 14px;
                            font-weight: 700;
                            color: $color-new-black;
                            text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
                            z-index: 2;

                            @media (max-width: $breakpoint-mobile) {
                                display: inline-block;
                                font-size: 16px;
                                right: 12px;
                            }
                        }
                    }

                    .loading-bar-glow {
                        position: absolute;
                        top: -3px;
                        left: 0;
                        height: calc(100% + 6px);
                        background: linear-gradient(
                            90deg,
                            $color-dt-light-blue-1 0%,
                            $color-dt-light-blue-2 25%,
                            $color-primary-dt-blue 50%,
                            $color-dt-dark-blue-1 75%,
                            $color-dt-dark-blue-2 100%
                        );
                        border-radius: 21px;
                        opacity: 0.4;
                        filter: blur(6px);
                        transition: width 0.15s ease-out;
                        z-index: -1;

                        @media (max-width: $breakpoint-mobile) {
                            border-radius: 27px;
                            filter: blur(8px);
                        }
                    }
                }
            }
        }

        @keyframes gradientShift {
            0% {
                background-position: 0% 50%;
            }
            50% {
                background-position: 100% 50%;
            }
            100% {
                background-position: 0% 50%;
            }
        }

        .loading-stuck-text {
            margin: 0;
            font-family: "TT Commons Pro", sans-serif;
            font-size: 16px;
            color: $color-new-black;
            text-align: center;
        }

        .loading-merch-img {
            width: 100%;
            max-width: 291px;

            @media (max-width: $breakpoint-mobile) {
                order: 3;
                max-width: 209px;
            }
        }
    }

    .merch-error {
        display: flex;
        flex-direction: column;
        align-items: center;
        row-gap: 16px;
        margin: 24px auto;
        padding: 16px;
        max-width: 560px;
        text-align: center;
        background: #f9e8e8;
        border: 1px solid #e2b3b3;
        border-radius: 12px;
        color: #8a2f2f;
        font-weight: 600;
        line-height: 1.4;

        p {
            margin: 0;
        }
    }

    .merch-content {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        
        &.is-changing {
            .mug-preview-wrapper,
            .plain-image-preview-wrapper {
                filter: blur(8px);
                transition: filter 0.5s ease-in;
            }
        }
        
        .mug-preview-wrapper {
            position: relative;
            width: 100%;
            max-width: 400px;
            margin: 0 auto;
            background: transparent;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: filter 0.5s ease-out;
            
            @media (max-width: $breakpoint-mobile) {
                max-width: 420px;
            }
            
            .mug-base {
                width: 100%;
                height: auto;
                display: block;
                z-index: 1;
                border-radius: 8px;
                border: 1px solid $color-normal-grey;
                background: $color-white;
            }
            
            .mug-overlay {
                position: absolute;
                z-index: 10;
                display: block;
                object-fit: contain;
                border-radius: 8px;
                pointer-events: none;
                max-width: 100%;
                max-height: 100%;
            }
        }

        .plain-image-preview-wrapper {
            width: 100%;
            max-width: 400px;
            margin: 0 auto;
            transition: filter 0.5s ease-out;

            @media (max-width: $breakpoint-mobile) {
                max-width: 420px;
            }

            &.plain-image-preview-wrapper--portrait {
                max-width: 360px;

                @media (max-width: $breakpoint-mobile) {
                    max-width: 340px;
                }
            }

            .plain-preview-image {
                width: 100%;
                height: auto;
                display: block;
                border-radius: 8px;
                border: 1px solid $color-normal-grey;
                background: $color-white;
            }

            &.plain-image-preview-wrapper--portrait .plain-preview-image {
                aspect-ratio: 2 / 3;
                object-fit: contain;
            }
        }
    }

    .change-indicator {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 80px;
        height: 80px;
        transform: translate(-50%, -50%);
        z-index: 10;
        opacity: 0;
        animation: fadeIn 0.5s ease-in forwards;
        display: flex;
        align-items: center;
        justify-content: center;

        
        @keyframes fadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }
        
        @keyframes spin {
            0% {
                transform: rotate(0deg);
            }
            100% {
                transform: rotate(360deg);
            }
        }
    }
}

.dev-control-panel {
    position: fixed;
    top: 24px;
    right: 24px;
    z-index: 9999;
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    min-width: 260px;
    font-size: 14px;
    .dev-control-row, .dev-control-subrow {
        display: flex;
        align-items: center;
        margin-bottom: 6px;
        label {
            min-width: 90px;
            margin-right: 8px;
        }
        input[type="number"] {
            width: 70px;
            margin-right: 8px;
        }
        input[type="range"] {
            width: 90px;
            margin-right: 8px;
        }
    }
    button {
        margin-top: 8px;
        padding: 4px 12px;
        font-size: 13px;
        border-radius: 4px;
        border: 1px solid #aaa;
        background: #f5f5f5;
        cursor: pointer;
    }
}

.song-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    
    &.is-changing {
        .song-lyrics-container {
            filter: blur(8px);
            transition: filter 0.5s ease-in;
        }
    }
    
    .song-lyrics-container {
        width: 100%;
        max-width: 600px;
        margin: 0 auto;
        padding: 24px;
        background: $color-white;
        border-radius: 12px;
        border: 1px solid $color-normal-grey;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        transition: filter 0.5s ease-out;
        
        @media (max-width: $breakpoint-mobile) {
            max-width: 100%;
            padding: 20px;
            border-radius: 8px;
        }
        
        .song-title {
            font-family: "TT Commons Pro", sans-serif;
            font-size: 24px;
            font-weight: 700;
            color: $color-new-black;
            text-align: center;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 2px solid $color-primary-dt-blue;
            
            @media (max-width: $breakpoint-mobile) {
                font-size: 20px;
                margin-bottom: 20px;
                padding-bottom: 12px;
            }
        }
        
        .song-structured {
            width: 100%;
            
            .song-section {
                margin-bottom: 32px;
                
                &:last-child {
                    margin-bottom: 0;
                }
                
                @media (max-width: $breakpoint-mobile) {
                    margin-bottom: 24px;
                }
                
                .song-section-title {
                    font-family: "TT Commons Pro", sans-serif;
                    font-size: 18px;
                    font-weight: 700;
                    color: $color-new-black;
                    margin-bottom: 12px;
                    padding-bottom: 8px;
                    border-bottom: 1px solid rgba($color-new-black, 0.3);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    
                    @media (max-width: $breakpoint-mobile) {
                        font-size: 16px;
                        margin-bottom: 10px;
                        padding-bottom: 6px;
                    }
                }
                
                .song-section-content {
                    font-family: "TT Commons Pro", sans-serif;
                    font-size: 16px;
                    line-height: 1.6;
                    color: $color-new-black;
                    white-space: pre-wrap;
                    margin: 0;
                    text-align: left;
                    padding-left: 16px;
                    
                    @media (max-width: $breakpoint-mobile) {
                        font-size: 15px;
                        line-height: 1.5;
                        padding-left: 12px;
                    }
                }
            }
        }
        

    }
}
</style>
